import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * The one body layout for a Settings tab page.
 *
 * Settings pages render inside SettingsLayout's panel, under the workspace's
 * own heading and tab strip. They used to cap their content at a fixed narrow
 * width and stack every section in one column, which left the right half of a
 * desktop screen empty and made the office scroll. This is the replacement:
 *
 *   SettingsPage   full width (no cap), the page padding, the gap between cards
 *   SettingsGrid   one column, two from a 1440px-wide window
 *   SettingsCard   a titled section; `wide` spans both columns (tables, lists)
 *   SettingsHelp   one helper sentence
 *   SettingsNotice one line, shown only when something is wrong, with its fix
 *
 * WHY 1440 AND NOT 1280. At a 1280 window the panel's content is about 700px
 * wide once the app sidebar and the Settings nav have taken their share, so
 * two columns would be about 345px each, which is narrower than a card that
 * already sets two fields side by side. At 1440 the columns are about 420px,
 * at 1920 about 650px. Approved in the batch-1 brief.
 *
 * WHY THE BREAKPOINT IS THE WINDOW AND NOT THE PANEL. A container query would
 * be the exact measure, but this Tailwind has no container-query plugin, and
 * adding one for a single breakpoint is a dependency for a rounding error.
 *
 * Layout and ink only. Nothing here reads or writes a setting.
 */

export function SettingsPage({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div data-testid="settings-page-body" className={cn('min-h-full space-y-4 px-6 py-4 pb-[var(--bottom-chrome)]', className)}>{children}</div>;
}

export function SettingsGrid({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div data-testid="settings-grid" className={cn('grid grid-cols-1 items-start gap-4 min-[1440px]:grid-cols-2', className)}>{children}</div>;
}

/** A column inside the grid holding more than one card, stacked. */
export function SettingsColumn({ children }: { children: React.ReactNode }) {
  return <div className="min-w-0 space-y-4">{children}</div>;
}

export interface SettingsCardProps {
  title: string;
  /** Heading id, so the section can be `aria-labelledby` it. */
  id?: string;
  icon?: React.ReactNode;
  /** One sentence under the heading. */
  help?: React.ReactNode;
  /** Right side of the heading row: a Save, an Add. */
  actions?: React.ReactNode;
  /** Span both grid columns. For tables and long lists. */
  wide?: boolean;
  /** Drop the body padding, for a table that runs edge to edge. */
  flush?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function SettingsCard({ title, id, icon, help, actions, wide, flush, children, className }: SettingsCardProps) {
  const headingId = id ?? `settings-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'min-w-0 overflow-hidden rounded-lg border border-fieldiq-border bg-fieldiq-card',
        wide && 'min-[1440px]:col-span-2',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-fieldiq-border px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {icon && <span className="text-fieldiq-accent" aria-hidden="true">{icon}</span>}
            {/* The white ink is remapped to the heading colour in light mode. */}
            <h2 id={headingId} className="text-base font-semibold text-white">{title}</h2>
          </div>
          {help && <SettingsHelp className="mt-1">{help}</SettingsHelp>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      <div className={flush ? undefined : 'space-y-4 p-5'}>{children}</div>
    </section>
  );
}

export function SettingsHelp({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={cn('text-xs text-fieldiq-muted', className)}>{children}</p>;
}

/**
 * One line, rendered only when something is actually wrong, with the control
 * that fixes it. The hue is on the edge, the wash and the glyph; the words are
 * in theme ink, because a signal hue as text fails on the light-mode card.
 */
export function SettingsNotice({
  tone,
  children,
  action,
  role,
  testId,
}: {
  tone: 'warning' | 'danger';
  children: React.ReactNode;
  action?: React.ReactNode;
  role?: 'alert' | 'status';
  testId?: string;
}) {
  return (
    <div
      role={role ?? (tone === 'danger' ? 'alert' : 'status')}
      data-testid={testId}
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-lg border px-4 py-2.5 text-sm text-fieldiq-text',
        tone === 'danger' ? 'border-red-500/40 bg-red-500/10' : 'border-amber-500/40 bg-amber-500/10',
      )}
    >
      <AlertTriangle
        size={16}
        aria-hidden="true"
        className={cn('shrink-0', tone === 'danger' ? 'text-fieldiq-danger' : 'text-fieldiq-accent-signal')}
      />
      <span className="min-w-[12rem] flex-1">{children}</span>
      {action && <span className="shrink-0">{action}</span>}
    </div>
  );
}
